import React, { useRef, useEffect, useState } from 'react';
import { ensureLogoIsBase64 } from '../../utils/logoConverter';

interface LogoUploaderProps {
  logo: string;
  onChange: (logo: string) => void;
  className?: string;
}

export const LogoUploader: React.FC<LogoUploaderProps> = ({
  logo,
  onChange,
  className = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [convertedLogo, setConvertedLogo] = useState<string>('');
  const [isLogoLoaded, setIsLogoLoaded] = useState<boolean>(false);

  // Convert logo to base64 if it's a URL
  useEffect(() => {
    if (logo) {
      setIsLogoLoaded(false);
      ensureLogoIsBase64(logo)
        .then(base64Logo => {
          setConvertedLogo(base64Logo);
          setIsLogoLoaded(true);
        })
        .catch(error => {
          console.warn('Failed to convert logo:', error);
          setConvertedLogo(logo); // Fallback to original
          setIsLogoLoaded(true);
        });
    } else {
      setConvertedLogo('');
      setIsLogoLoaded(true);
    }
  }, [logo]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onChange(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    onChange('');
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
      
      {convertedLogo ? (
        <div className="relative group inline-block">
          <img
            src={convertedLogo}
            alt="Company Logo"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              display: 'block',
              maxWidth: '80px',
              maxHeight: '80px'
            }}
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => fileInputRef.current?.click()}
            onLoad={() => setIsLogoLoaded(true)}
            onError={() => {
              console.warn('Logo image failed to load');
              setIsLogoLoaded(true);
            }}
          />
          <button
            onClick={handleRemoveLogo}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            ×
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
        >
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-1">📷</div>
            <div className="text-xs">Click to upload logo</div>
          </div>
        </div>
      )}
    </div>
  );
};
